// src/components/LanguageSwitcher.js

import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { trackEvent } from '../lib/analytics';

const SwitcherContainer = styled(motion.div)`
  position: fixed;
  top: clamp(1.5rem, 3vw, 2rem);
  left: clamp(1.5rem, 3vw, 2rem);
  display: flex;
  gap: 0.75rem;
  z-index: 10;
  font-family: var(--font-body);
  will-change: opacity, transform;
`;

const LangButton = styled(motion.button)`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  color: ${({ $isActive }) => ($isActive ? '#FFFFFF' : '#888888')};
  transition: color 0.3s ease-in-out;
  text-shadow: 0px 1px 4px rgba(0, 0, 0, 0.7);

  &:hover {
    color: #FFFFFF;
  }
`;

const Separator = styled.span`
  color: #888888;
  font-size: 1.1rem;
`;

const LanguageSwitcher = ({ concealed = false }) => {
  const { i18n } = useTranslation();

  const changeLanguage = (lng) => {
    if (!i18n.language.startsWith(lng)) {
      trackEvent('navigation_click', {
        path: window.location.pathname,
        target: lng,
        label: lng.toUpperCase(),
        nav_type: 'language',
      });
    }
    i18n.changeLanguage(lng);
  };

  return (
    <SwitcherContainer
      aria-label="Language switcher"
      data-hidden={concealed ? 'true' : 'false'}
      style={{ pointerEvents: concealed ? 'none' : 'auto' }}
      initial={{ opacity: 0, y: -12 }}
      animate={{
        opacity: concealed ? 0 : 1,
        y: concealed ? -18 : 0,
      }}
      transition={{ duration: 0.28, ease: [0.22, 0.61, 0.36, 1] }}
    >
      <LangButton
        onClick={() => changeLanguage('en')}
        $isActive={i18n.language.startsWith('en')}
        whileTap={{ scale: 0.94 }}
      >
        <AnimatePresence mode="wait" initial={false}>
          <motion.span
            key={i18n.language.startsWith('en') ? 'EN-on' : 'EN-off'}
            initial={{ y: 8, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -8, opacity: 0 }}
            transition={{ duration: 0.18 }}
          >
            EN
          </motion.span>
        </AnimatePresence>
      </LangButton>
      <Separator>|</Separator>
      <LangButton
        onClick={() => changeLanguage('nl')}
        $isActive={i18n.language.startsWith('nl')}
        whileTap={{ scale: 0.94 }}
      >
        <AnimatePresence mode="wait" initial={false}>
          <motion.span
            key={i18n.language.startsWith('nl') ? 'NL-on' : 'NL-off'}
            initial={{ y: 8, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -8, opacity: 0 }}
            transition={{ duration: 0.18 }}
          >
            NL
          </motion.span>
        </AnimatePresence>
      </LangButton>
    </SwitcherContainer>
  );
};

export default LanguageSwitcher;
